/**
 * Every user-facing string in the PWA, in one file (PLAN §5).
 *
 * Rules this file exists to make checkable:
 *  - 繁體中文 only. `test/i18n-simplified.test.ts` fails on simplified forms.
 *  - No 繁中 literal anywhere else under `src/` — `test/i18n-single-home.test.ts`
 *    scans for CJK outside this directory. Comments in `src/` stay English so
 *    that lint stays honest rather than merely quiet.
 *  - Strings frozen by SPEC/CONTRACTS are reproduced verbatim and marked.
 *
 * Interpolation is plain functions, not a template DSL: one less runtime, and
 * the argument types are checked by tsc.
 */

export const zh = {
  app: {
    name: 'Omni AI 員工',
    loading: '載入中…',
    cancel: '取消',
    save: '儲存',
    saving: '儲存中…',
    close: '關閉',
    back: '返回',
    copied: '已複製',
    download: '下載',
    empty: '目前沒有內容',
    notFoundTitle: '找不到這個頁面',
    notFoundBody: '你要找的內容不存在，或你沒有查看的權限。',
    unknownError: '系統發生未預期的錯誤，請稍後再試。',
    logout: '登出',
    skipToContent: '跳到主要內容',
  },

  tabs: {
    navLabel: '主要導覽',
    chat: '對話',
    memory: '記憶',
    spaces: '空間',
    settings: '設定',
    admin: '管理',
  },

  connection: {
    /** SPEC §10 — verbatim. */
    reconnecting: '重新連線中…',
    /** SPEC §10 — verbatim. */
    offline: '已離線，恢復連線後可繼續',
  },

  banner: {
    credentialAbsent: '尚未設定 Anthropic 憑證，請聯絡管理員後再開始對話。',
    credentialInvalid: 'Anthropic 憑證已失效，請聯絡管理員更新。',
    /** SPEC §11 — 429 → amber, 15 minutes, no auto-retry. */
    credentialRateLimited: 'Anthropic 暫時限制了流量，約 15 分鐘後恢復，這段期間無法開始新的對話。',
    maintenanceDefault: '系統維護中，暫時無法開始新的對話。',
    maintenance: (message: string) => `系統維護中：${message}`,
    pendingRecreate: '權限或設定已變更，下一次對話會用新的容器啟動。',
    /** SPEC §10 — verbatim. */
    updateReady: '有新版本，點一下更新',
    update: '更新',
    dismiss: '知道了',
  },

  login: {
    title: '登入',
    subtitle: '請用公司電子郵件登入你的 AI 員工。',
    email: '電子郵件',
    password: '密碼',
    submit: '登入',
    submitting: '登入中…',
    emailPlaceholder: 'you@omniingredients.com',
    missingFields: '請輸入電子郵件與密碼。',
  },

  firstRun: {
    title: '請先設定新密碼',
    subtitle: '這是第一次登入，請把一次性密碼換成只有你知道的密碼。',
    currentPassword: '目前密碼（一次性密碼）',
    newPassword: '新密碼',
    confirmPassword: '再輸入一次新密碼',
    submit: '設定新密碼',
    mismatch: '兩次輸入的新密碼不一樣。',
    tooShort: '新密碼至少需要 12 個字元。',
  },

  threads: {
    title: '對話',
    newThread: '新對話',
    creating: '建立中…',
    emptyTitle: '還沒有任何對話',
    emptyBody: '按右下角的「新對話」開始交代第一件事。',
    untitled: '未命名對話',
    pendingApproval: '有待審批',
    archived: '已封存',
    showArchived: '顯示已封存',
    hideArchived: '隱藏已封存',
    archive: '封存',
    unarchive: '取消封存',
    rename: '重新命名',
    renameTitle: '重新命名對話',
    loadMore: '載入更多',
    messageCount: (n: number) => `${n} 則訊息`,
    lastActive: (when: string) => `最後活動 ${when}`,
    noCostYet: '尚未計費',
    /** The desktop list pane (SPEC §10's first pane). */
    listLabel: '對話列表',
    /** The second pane on 對話列表, where no conversation is open yet. */
    detailPlaceholderTitle: '挑一則對話',
    detailPlaceholderBody: '從左邊的列表選一則對話，或建立新的對話。',
  },

  chat: {
    backToList: '回到對話列表',
    /** SPEC §10 R21 — shown until the first text delta of a turn. */
    thinking: '思考中…',
    /** SPEC §10 — verbatim pill copy. */
    newMessages: '↓ 有新訊息',
    emptyTitle: '交代第一件事給你的 AI 員工',
    emptyBody: '它會記住你說過的話，也會在動手前先問你。',
    starters: [
      '幫我整理今天的重點，並寫進任務紀錄。',
      '請記住：我負責的產品線是植物萃取（Botanical Extracts）。',
      '看一下共享空間裡最近改了什麼。',
    ],
    composerPlaceholder: '輸入訊息…（⌘/Ctrl + Enter 送出）',
    send: '送出',
    stop: '停止',
    stopping: '中斷中…',
    /** SPEC §10 — disabled composer states carry the reason inline. */
    disabledNoCredential: '憑證未設定',
    disabledStarting: '啟動中',
    disabledOffline: '已離線',
    disabledBudget: '額度已滿',
    disabledMaintenance: '維護中',
    disabledTurnRunning: '對話進行中',
    tooLong: (max: number) => `訊息太長了，請縮短到 ${max} 個字以內。`,
    interrupted: (reason: string) => `本次對話已中斷（${reason}）`,
    interruptReason: {
      user: '你按了停止',
      timeout: '逾時',
      shutdown: '系統重啟',
      /** SPEC §11 — matrix reduction mid-turn. */
      revocation: '權限已變更，本次對話已中止',
    },
    turnFailed: '這一回合沒有完成。',
    /** SPEC §11 — verbatim shape; the dollar half only renders when budgetsEnforced. */
    resultMaxBudget: (limit: string) => `這次對話已達花費上限（${limit}），可在設定調整`,
    resultMaxTurnsDetail: (turns: number) => `做了 ${turns} 個步驟還沒完成，請把任務拆小一點`,
    userLabel: '你',
    assistantLabel: 'AI 員工',
    systemLabel: '系統',
  },

  tool: {
    /** SPEC §10 — per-message 活動摘要 strip. */
    activityTitle: '活動摘要',
    activityRead: '讀',
    activityWrite: '寫',
    activityCommand: '指令',
    activityOther: '其他',
    /** SPEC §10 — consecutive same-tool calls group as 「讀取檔案 ×7」. */
    group: (label: string, count: number) => `${label} ×${count}`,
    expand: '展開細節',
    collapse: '收合',
    input: '輸入',
    result: '結果',
    noResult: '（沒有輸出）',
    running: '進行中…',
    denied: '已拒絕',
    interrupted: '已中斷',
    failed: '失敗',
    ok: '完成',
    autoAllowedGrant: '已在自動核准清單',
    autoAllowedMode: '權限模式自動允許',
    duration: (seconds: string) => `${seconds}s`,
    resultBytes: (text: string) => `輸出 ${text}`,
    /** SPEC §10/§11 — the whole read-only story in one line. */
    readOnlySpace: '這個空間你只有唯讀權限',
    copyInput: '複製輸入',
  },

  approval: {
    title: '需要你的核准',
    riskHigh: '高風險',
    /** SPEC §10 — countdown ring copy, verbatim shape. */
    countdown: (mmss: string) => `${mmss} 後自動拒絕`,
    allowOnce: '允許一次',
    /** SPEC §10 — verbatim. */
    allowAlways: '一律允許此工具（不限參數）',
    deny: '拒絕',
    denyNoteLabel: '拒絕原因（選填）',
    /** SPEC §10 — verbatim. */
    denyNoteHint: '你的說明會傳給 AI 員工',
    denySubmit: '送出拒絕',
    alwaysConfirmTitle: '要一律允許這個工具嗎？',
    alwaysConfirmBody: (label: string) =>
      `之後「${label}」不論參數都會直接執行，不再詢問你。你可以隨時到「設定 → 自動核准清單」收回。`,
    alwaysConfirmOk: '確定，一律允許',
    resolving: '處理中…',
    resolvedAllowOnce: '你允許了這一次',
    resolvedAllowAlways: '你設為一律允許',
    resolvedDeny: '你拒絕了這次請求',
    /** SPEC §11 — verbatim. */
    resolvedTimeout: '審批逾時（10 分鐘未回應），已自動拒絕。',
    resolvedInterrupt: '對話被中斷，這個請求已自動拒絕。',
    resolvedShutdown: '系統重啟，這個請求已自動拒絕。',
    resolvedElsewhere: '已在其他裝置處理',
    /** Composed here, not at the call site, so the whole sentence stays in one file. */
    resolvedWithSource: (base: string, source: string) => `${base}（${source}）`,
    /** SPEC §11 — 410 APPROVAL_GONE. */
    gone: '這個請求已失效',
    noteWithText: (note: string) => `你的說明：${note}`,
    pendingCount: (n: number) => `${n} 件待審批`,
    notificationTitle: 'AI 員工需要你的核准',
    notificationBody: (label: string) => `「${label}」正在等你回覆。`,
  },

  cost: {
    /** SPEC §11 / CONTRACTS §4.1 — no fake $0.00 under a subscription credential. */
    subscription: '訂閱額度',
    /** SPEC §11 — the usage meter is replaced by this whenever budgetsEnforced is false. */
    subscriptionMeter: '訂閱額度制',
    steps: (n: number) => `${n} 步驟`,
    seconds: (s: string) => `${s} 秒`,
    tokens: (input: string, output: string) => `輸入 ${input} · 輸出 ${output}`,
    cacheRead: (n: string) => `快取讀取 ${n}`,
    todayCost: (amount: string) => `今日已用 ${amount}`,
  },

  memory: {
    title: '記憶',
    tabIndex: '索引',
    tabFiles: '檔案',
    tabClaudeMd: '工作守則',
    tabTaskLog: '任務紀錄',
    tabOutput: '產出',
    indexTitle: 'memory/MEMORY.md 索引',
    indexEmpty: '索引還是空的——等 AI 員工第一次記下東西就會出現。',
    indexMissingFile: '索引指到的檔案不存在',
    orphans: '沒有列進索引的檔案',
    orphansHint: '這些檔案存在，但 MEMORY.md 沒有列到它們。',
    updatedAt: (when: string) => `更新於 ${when}`,
    fileEmpty: '這個檔案是空的。',
    filesEmpty: '這個資料夾還沒有檔案。',
    notText: '這是二進位檔案，無法直接顯示，請改用下載。',
    truncated: '內容過長，只顯示前面一部分。完整內容請下載檔案。',
    outputEmpty: '產出資料夾還是空的。',
    taskLogEmpty: '任務紀錄還是空的。',
    taskLogTail: (n: number) => `顯示最後 ${n} 行`,
    /** SPEC §10 — 記憶足跡 chips from workspace.changed. */
    footprintTitle: '記憶足跡',
    footprintCreated: (name: string) => `已新增記憶：${name}`,
    footprintModified: (name: string) => `已更新記憶：${name}`,
    footprintDeleted: (name: string) => `已刪除：${name}`,
    openInMemory: '在「記憶」中開啟',
    breadcrumbRoot: '工作區',
  },

  claudeMd: {
    title: '工作守則（CLAUDE.md）',
    subtitle: '這是唯一可以在網頁上編輯的檔案。每次儲存都會留下版本。',
    preview: '預覽變更',
    hideDiff: '收起變更',
    revisions: '版本',
    revisionsTitle: '歷史版本（最近 20 筆）',
    restore: '還原這個版本',
    restoreConfirm: '確定要還原成這個版本嗎？目前的內容會先存成一個新版本。',
    restored: '已還原。',
    restoring: '還原中…',
    authorUser: '你',
    authorAgent: 'AI 員工',
    authorSeed: '初始範本',
    /** CONTRACTS §2 STALE — the optimistic-lock conflict, 409. */
    stale: '這份內容在你編輯期間被更新過，請重新載入後再儲存。',
    staleKeepDraft: '你剛剛打的內容還在下面，重新載入前可以先複製起來。',
    reload: '重新載入最新版本',
    tooLarge: (kb: number) => `內容超過 ${kb} KB 上限，請先精簡。`,
    unsaved: '有未儲存的變更',
    lintTitle: '結構檢查',
    lintClean: '結構看起來沒問題。',
    lintMissingHeading: (heading: string) => `少了「${heading}」這個段落`,
    lintPlaceholder: (name: string) => `還有沒有替換的佔位符 {{${name}}}`,
    lintEmpty: '內容是空的，AI 員工會失去工作守則。',
    lintTooLarge: (kb: number) => `內容 ${kb} KB，已接近或超過上限`,
    diffAdded: '新增',
    diffRemoved: '刪除',
    diffNoChange: '沒有變更',
    /** Kept here rather than in the linter so the simplified-character lint sees them. */
    requiredHeadings: [
      '## 你的角色',
      '## 工作區結構',
      '## 任務紀錄習慣（task-log.md）',
      '## 記憶習慣（memory/）',
      '## 共享空間規矩',
      '## 輸出與語氣',
      '## 一致性紀律',
    ],
  },

  spaces: {
    title: '共享空間',
    empty: '你目前沒有任何共享空間的權限。',
    emptyHint: '需要存取部門或公司空間，請聯絡管理員。',
    levelRo: '唯讀',
    levelRw: '可寫',
    kindCompany: '公司',
    kindDepartment: '部門',
    lastCommit: (when: string) => `最後更新 ${when}`,
    sizeLabel: (size: string) => `大小 ${size}`,
    tabFiles: '檔案',
    /** SPEC §10 — 版本紀錄. */
    tabHistory: '版本紀錄',
    historyEmpty: '這個空間還沒有版本紀錄。',
    historyFiles: (n: number) => `${n} 個檔案`,
    viewAtCommit: '看這個版本的內容',
    backToCurrent: '回到最新版本',
    blobTitle: (sha: string) => `版本 ${sha} 的內容`,
    denied: '你沒有這個共享空間的權限。',
    readOnlyNotice: '你對這個空間只有唯讀權限，AI 員工也一樣不能寫入。',
  },

  settings: {
    title: '設定',
    sectionEmployee: 'AI 員工',
    sectionModel: '模型與權限',
    sectionBudget: '費用上限',
    sectionMcp: 'MCP 伺服器',
    sectionGrants: '自動核准清單',
    sectionRunner: '容器狀態',
    sectionAccount: '帳號',
    sectionPassword: '修改密碼',
    sectionInstall: '安裝 App',
    displayName: '員工名稱',
    displayNameHint: '這是你在對話裡稱呼它的名字。',
    model: '模型',
    permissionMode: '權限模式',
    permissionModeDefault: '每次動手前先問我',
    permissionModeAcceptEdits: '檔案編輯免問，其它照問',
    permissionModePlan: '只規劃，不動手',
    permissionModeHint: '不論選哪一種，共享空間的唯讀權限都是由容器掛載強制的，改設定不會鬆開。',
    maxTurns: '每次對話最多步驟數',
    maxTurnsHint: '不論憑證是哪一種，這個上限都會生效。',
    maxBudget: '每次對話金額上限（USD）',
    dailyBudget: '每日金額上限（USD）',
    dailyBudgetHint: '每日上限由管理員設定。',
    /** CONTRACTS §4.1 / SPEC §11 — verbatim, shown whenever budgetsEnforced is false. */
    budgetsDisabledNotice: '訂閱制憑證：金額上限不會生效',
    budgetsDisabledDetail:
      '目前的憑證是訂閱制（OAuth），Anthropic 不會回報金額，所以金額上限無法生效。實際生效的是步驟數上限、每 5 分鐘 20 次對話的速率限制、同時 3 位員工的容量上限，以及訂閱方案本身的額度。若需要金額上限，請請管理員改用 API key。',
    budgetCeiling: (amount: string) => `上限最高可設到 ${amount}`,
    usageTitle: '用量',
    usageEmpty: '還沒有用量紀錄。',
    usageDay: '日期',
    usageTurns: '對話回合',
    usageCost: '花費',
    mcpHint: '以 JSON 編輯。密碼與金鑰欄位會顯示成遮罩，直接儲存就會保留原本的值。',
    mcpInvalidJson: 'JSON 格式不正確，請檢查括號與逗號。',
    mcpEmpty: '目前沒有設定任何 MCP 伺服器。',
    mcpStatusUnknown: '尚未連線',
    mcpStatusConnected: '連線正常',
    mcpStatusFailed: '連線失敗',
    mcpToolCount: (n: number) => `${n} 個工具`,
    mcpHasSecret: '含密鑰',
    mcpMaskHint: '遮罩值保持不變即可保留原本的密鑰。',
    grantsEmpty: '目前沒有任何工具在自動核准清單裡。',
    grantsHint: '在清單裡的工具不會再跳出審批卡片。',
    grantsUsed: (n: number) => `已使用 ${n} 次`,
    grantsNeverUsed: '還沒用過',
    grantsRevoke: '收回',
    grantsRevoked: '已收回，下一次對話生效。',
    runnerState: '狀態',
    runnerVolume: '工作區用量',
    runnerLimits: (memoryMb: number, cpu: string, pids: number) =>
      `記憶體 ${memoryMb} MB · CPU ${cpu} · 程序上限 ${pids}`,
    runnerMounts: '已掛載的共享空間',
    runnerMountEntry: (slug: string, level: string) => `${slug}（${level}）`,
    runnerNoMounts: '目前沒有掛載任何共享空間。',
    runnerRestart: '重新啟動容器',
    runnerRestarting: '重新啟動中…',
    runnerRestartBusy: '目前有對話進行中，要先中斷再重新啟動嗎？',
    runnerRestartForce: '中斷並重新啟動',
    /** SPEC §11 — volume soft limit notice. */
    volumeWarning: (usedMb: string) => `你的工作區已使用 ${usedMb} MB，請整理 output/`,
    currentPassword: '目前密碼',
    newPassword: '新密碼',
    confirmPassword: '再輸入一次新密碼',
    changePassword: '更新密碼',
    passwordChanged: '密碼已更新，其他裝置已登出。',
    installHint: '把 Omni AI 員工加到主畫面，用起來就像一般 App。',
    installAction: '加到主畫面',
    installIosHint: '在 Safari 按「分享」→「加入主畫面」。',
    installed: '已安裝。',
  },

  runnerState: {
    absent: '尚未啟動',
    creating: '建立中',
    starting: '啟動中',
    healthy: '就緒',
    busy: '工作中',
    draining: '收尾中',
    stopped: '已停止',
    error: '異常',
  },


  admin: {
    /** PLAN §5 frozen seam — rendered when `import.meta.glob` finds no AdminRoutes. */
    notInstalled: '管理功能尚未安裝',
    notInstalledHint: '這個版本沒有包含管理介面。',
  },


  time: {
    justNow: '剛剛',
    minutesAgo: (n: number) => `${n} 分鐘前`,
    hoursAgo: (n: number) => `${n} 小時前`,
    daysAgo: (n: number) => `${n} 天前`,
  },

  units: {
    bytes: (n: string) => `${n} B`,
    kb: (n: string) => `${n} KB`,
    mb: (n: string) => `${n} MB`,
    gb: (n: string) => `${n} GB`,
  },
} as const;

export type Zh = typeof zh;
